import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import api from "../../api";
import { FIRST_NAME_TAG } from "./emailDefaults";

/**
 * The invitation email as a respondent receives it. The HTML comes from the
 * BE, rendered through the SAME template and substitutions the send uses, so
 * this can't drift from what is actually delivered; only the recipient is a
 * sample ("Alex", a dummy link). It renders in a sandboxed iframe so the
 * email's own styles neither leak into nor inherit from the page.
 */
const EmailPreviewDialog = ({
  open,
  onClose,
  surveyId,
  collectorId,
  subject,
  body,
  showSurveyButton,
  surveyButtonText,
}) => {
  const { formatMessage } = useIntl();
  const [state, setState] = useState({ loading: false, preview: null, failed: false });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState({ loading: true, preview: null, failed: false });
    api
      .emailPreview(surveyId, collectorId, { subject, body, showSurveyButton, surveyButtonText })
      .then((result) => {
        if (cancelled) return;
        setState({ loading: false, preview: result.ok ? result.data : null, failed: !result.ok });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, preview: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
    // Fetch once per opening, for the draft as it is at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { loading, preview, failed } = state;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <FormattedMessage id="collector_configure.preview.title" />
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {failed && (
          <Typography color="error">
            <FormattedMessage id="collector_configure.preview.error" />
          </Typography>
        )}
        {preview && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>
                <FormattedMessage id="collector_configure.subject" />:
              </strong>{" "}
              <span data-testid="email-preview-subject">{preview.subject}</span>
            </Typography>
            <Box
              component="iframe"
              title={formatMessage({ id: "collector_configure.preview.title" })}
              data-testid="email-preview-frame"
              sandbox=""
              srcDoc={preview.html}
              sx={{ width: "100%", height: { xs: 420, md: 560 }, border: "1px solid #e0e0e0", borderRadius: 1 }}
            />
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
              <FormattedMessage
                id="collector_configure.preview.note"
                values={{
                  tag: (
                    <Box component="code" sx={{ fontFamily: "monospace" }}>
                      {FIRST_NAME_TAG}
                    </Box>
                  ),
                }}
              />
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="collector_configure.preview.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPreviewDialog;
